/**
 * Governance Dashboard — DAO Proposal & Voting Interface
 * Reads from BHermesGovernor contract on Base Sepolia
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Vote, Plus, RefreshCw, CheckCircle2, XCircle,
  Clock, Zap, Shield, Gavel, ExternalLink, Copy, Users,
  ThumbsUp, ThumbsDown, Minus, AlertCircle, Loader2, Landmark, Settings,
} from 'lucide-react';
import {
  getGovernanceConfig, listProposals, getProposal,
  getVotingPower, createProposal, castVote, queueProposal,
  executeProposal, cancelProposal,
} from '@/lib/governanceApi';
import TerminalFloorNav from '../components/TerminalFloorNav';

const fmt = (n) => new Intl.NumberFormat().format(Math.round(n || 0));
const fmtDec = (n) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n || 0);

const STATE_CONFIG = {
  PENDING:   { color: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30', icon: Clock,        label: 'Pending' },
  ACTIVE:    { color: 'bg-blue-500/15 text-blue-600 border-blue-500/30',       icon: Vote,         label: 'Active' },
  DEFEATED:  { color: 'bg-red-500/15 text-red-600 border-red-500/30',          icon: XCircle,      label: 'Defeated' },
  SUCCEEDED: { color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30', icon: CheckCircle2, label: 'Succeeded' },
  QUEUED:    { color: 'bg-orange-500/15 text-orange-600 border-orange-500/30',  icon: Clock,        label: 'Queued' },
  EXECUTED:  { color: 'bg-teal-500/15 text-teal-600 border-teal-500/30',       icon: Zap,          label: 'Executed' },
  CANCELLED: { color: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30',       icon: XCircle,      label: 'Cancelled' },
};

const StateBadge = ({ state }) => {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.PENDING;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`${cfg.color} gap-1 font-medium`} data-testid={`state-badge-${state}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
};

// ─── KPI Card ───
const KpiCard = ({ title, value, subtitle, icon: Icon, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-5 pb-4 px-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl md:text-3xl heading-font tabular-nums font-semibold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── Proposal Card ───
const ProposalCard = ({ proposal, onSelect }) => {
  const totalVotes = proposal.for_votes + proposal.against_votes + proposal.abstain_votes;
  const forPct = totalVotes > 0 ? (proposal.for_votes / totalVotes) * 100 : 0;
  const againstPct = totalVotes > 0 ? (proposal.against_votes / totalVotes) * 100 : 0;

  return (
    <Card
      className="cursor-pointer transition-all hover:border-[hsl(var(--web3-primary))]/40 hover:shadow-md"
      onClick={() => onSelect(proposal)}
      data-testid={`proposal-card-${proposal.id}`}
    >
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-muted-foreground font-mono">#{proposal.id}</span>
              <StateBadge state={proposal.state} />
            </div>
            <p className="text-sm font-medium leading-snug line-clamp-2">{proposal.description}</p>
          </div>
        </div>

        {totalVotes > 0 && (
          <div className="space-y-2 mt-3">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3 text-emerald-500" /> {fmtDec(proposal.for_votes)}</span>
              <span className="flex items-center gap-1"><ThumbsDown className="h-3 w-3 text-red-500" /> {fmtDec(proposal.against_votes)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
              <div className="bg-emerald-500 transition-all" style={{ width: `${forPct}%` }} />
              <div className="bg-red-500 transition-all" style={{ width: `${againstPct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground text-right">{fmtDec(totalVotes)} total votes</p>
          </div>
        )}

        {totalVotes === 0 && (
          <p className="text-xs text-muted-foreground mt-2">No votes yet</p>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
          <span>Proposer: {proposal.proposer?.slice(0, 6)}...{proposal.proposer?.slice(-4)}</span>
          <span>Block {fmt(proposal.start_block)} - {fmt(proposal.end_block)}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Proposal Detail Modal ───
const ProposalDetailModal = ({ proposal, open, onClose, onAction }) => {
  const [voting, setVoting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  if (!proposal) return null;

  const totalVotes = proposal.for_votes + proposal.against_votes + proposal.abstain_votes;
  const forPct = totalVotes > 0 ? (proposal.for_votes / totalVotes) * 100 : 0;
  const againstPct = totalVotes > 0 ? (proposal.against_votes / totalVotes) * 100 : 0;
  const abstainPct = totalVotes > 0 ? (proposal.abstain_votes / totalVotes) * 100 : 0;

  const handleVote = async (voteType) => {
    setVoting(true);
    try {
      await castVote(proposal.id, voteType);
      const voteLabel = ['Against', 'For', 'Abstain'][voteType];
      toast.success(`Vote cast: ${voteLabel}`);
      onAction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cast vote');
    } finally {
      setVoting(false);
    }
  };

  const handleLifecycle = async (action) => {
    setActionLoading(action);
    try {
      if (action === 'queue') await queueProposal(proposal.id);
      if (action === 'execute') await executeProposal(proposal.id);
      if (action === 'cancel') await cancelProposal(proposal.id);
      toast.success(`Proposal ${action}d successfully`);
      onAction();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action} proposal`);
    } finally {
      setActionLoading(null);
    }
  };

  const blockProgress = proposal.current_block < proposal.start_block
    ? 0
    : proposal.current_block > proposal.end_block
      ? 100
      : ((proposal.current_block - proposal.start_block) / (proposal.end_block - proposal.start_block)) * 100;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="proposal-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 heading-font">
            <Gavel className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
            Proposal #{proposal.id}
          </DialogTitle>
          <DialogDescription className="sr-only">Proposal details and voting</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <StateBadge state={proposal.state} />
            <span className="text-xs text-muted-foreground font-mono">
              by {proposal.proposer?.slice(0, 8)}...{proposal.proposer?.slice(-6)}
            </span>
          </div>

          <p className="text-sm leading-relaxed">{proposal.description}</p>

          <Separator />

          {/* Voting progress */}
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Vote Breakdown</p>
            <VoteBar label="For" value={proposal.for_votes} pct={forPct} color="bg-emerald-500" icon={ThumbsUp} />
            <VoteBar label="Against" value={proposal.against_votes} pct={againstPct} color="bg-red-500" icon={ThumbsDown} />
            <VoteBar label="Abstain" value={proposal.abstain_votes} pct={abstainPct} color="bg-zinc-400" icon={Minus} />
            <p className="text-xs text-muted-foreground text-right">Total: {fmtDec(totalVotes)} HERMES</p>
          </div>

          <Separator />

          {/* Block timeline */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Voting Timeline</p>
            <Progress value={blockProgress} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground font-mono">
              <span>Start: {fmt(proposal.start_block)}</span>
              <span>Now: {fmt(proposal.current_block)}</span>
              <span>End: {fmt(proposal.end_block)}</span>
            </div>
          </div>

          {/* Actions */}
          {proposal.state === 'ACTIVE' && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Cast Your Vote</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  className="border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-600"
                  disabled={voting}
                  onClick={() => handleVote(1)}
                  data-testid="vote-for-btn"
                >
                  {voting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-1" />}
                  For
                </Button>
                <Button
                  variant="outline"
                  className="border-red-500/40 hover:bg-red-500/10 text-red-600"
                  disabled={voting}
                  onClick={() => handleVote(0)}
                  data-testid="vote-against-btn"
                >
                  {voting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-1" />}
                  Against
                </Button>
                <Button
                  variant="outline"
                  className="border-zinc-500/40 hover:bg-zinc-500/10 text-zinc-600"
                  disabled={voting}
                  onClick={() => handleVote(2)}
                  data-testid="vote-abstain-btn"
                >
                  {voting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Minus className="h-4 w-4 mr-1" />}
                  Abstain
                </Button>
              </div>
            </div>
          )}

          {/* Lifecycle actions */}
          <div className="flex gap-2 flex-wrap">
            {proposal.state === 'SUCCEEDED' && (
              <Button size="sm" onClick={() => handleLifecycle('queue')} disabled={!!actionLoading} data-testid="queue-btn">
                {actionLoading === 'queue' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
                Queue for Execution
              </Button>
            )}
            {proposal.state === 'QUEUED' && (
              <Button size="sm" onClick={() => handleLifecycle('execute')} disabled={!!actionLoading} data-testid="execute-btn">
                {actionLoading === 'execute' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
                Execute
              </Button>
            )}
            {!['EXECUTED', 'CANCELLED', 'DEFEATED'].includes(proposal.state) && (
              <Button
                size="sm" variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
                onClick={() => handleLifecycle('cancel')}
                disabled={!!actionLoading}
                data-testid="cancel-btn"
              >
                {actionLoading === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                Cancel
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VoteBar = ({ label, value, pct, color, icon: Icon }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1 font-medium"><Icon className="h-3 w-3" /> {label}</span>
      <span className="text-muted-foreground tabular-nums">{fmtDec(value)} HERMES ({Math.round(pct)}%)</span>
    </div>
    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
      <div className={`${color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

// ─── Create Proposal Dialog ───
const CreateProposalDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [desc, setDesc] = useState('');
  const [target, setTarget] = useState('');
  const [calldata, setCalldata] = useState('0x');

  const handleSubmit = async () => {
    if (!desc.trim()) { toast.error('Description is required'); return; }
    if (!target.trim()) { toast.error('Target address is required'); return; }
    setLoading(true);
    try {
      const result = await createProposal(desc, [target], [0], [calldata || '0x']);
      toast.success(`Proposal #${result.proposal_id} created!`);
      setOpen(false);
      setDesc('');
      setTarget('');
      setCalldata('0x');
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="create-proposal-btn">
          <Plus className="h-4 w-4" /> New Proposal
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="create-proposal-modal">
        <DialogHeader>
          <DialogTitle className="heading-font">Create Governance Proposal</DialogTitle>
          <DialogDescription>
            Submit a new proposal for $HERMES token holders to vote on.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="e.g., Authorize platform XYZ to write reputation scores..."
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={3}
              data-testid="proposal-description-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Target Contract Address</Label>
            <Input
              placeholder="0x..."
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="font-mono text-sm"
              data-testid="proposal-target-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Calldata (hex)</Label>
            <Input
              placeholder="0x..."
              value={calldata}
              onChange={(e) => setCalldata(e.target.value)}
              className="font-mono text-sm"
              data-testid="proposal-calldata-input"
            />
            <p className="text-xs text-muted-foreground">Encoded function call. Use 0x for no-op proposals.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="submit-proposal-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Gavel className="h-4 w-4 mr-1" />}
            Submit Proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Voting Power Checker ───
const VotingPowerCard = () => {
  const [wallet, setWallet] = useState('');
  const [power, setPower] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    if (!wallet.trim()) return;
    setLoading(true);
    try {
      const data = await getVotingPower(wallet.trim());
      setPower(data);
    } catch {
      toast.error('Failed to check voting power');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="voting-power-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base heading-font flex items-center gap-2">
          <Users className="h-4 w-4 text-[hsl(var(--web3-primary))]" /> Voting Power
        </CardTitle>
        <CardDescription className="text-xs">Check $HERMES balance and proposal eligibility</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Wallet address (0x...)"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            className="font-mono text-sm"
            data-testid="voting-power-wallet-input"
          />
          <Button size="sm" onClick={check} disabled={loading} data-testid="check-power-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check'}
          </Button>
        </div>
        {power && (
          <div className="p-3 rounded-lg bg-muted/50 space-y-2" data-testid="voting-power-result">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Voting Power</span>
              <span className="font-semibold tabular-nums heading-font">{fmtDec(power.voting_power)} HERMES</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Proposal Threshold</span>
              <span className="tabular-nums">{fmtDec(power.proposal_threshold)} HERMES</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Can Propose</span>
              {power.can_propose
                ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Yes</Badge>
                : <Badge variant="outline" className="bg-red-500/15 text-red-600 border-red-500/30">No</Badge>
              }
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

export default function GovernanceDashboardPage({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cfg, props] = await Promise.all([getGovernanceConfig(), listProposals()]);
      setConfig(cfg);
      setProposals(props);
    } catch (err) {
      toast.error('Failed to load governance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = () => { setRefreshing(true); fetchData(); };

  const handleSelectProposal = async (p) => {
    try {
      const fresh = await getProposal(p.id);
      setSelectedProposal(fresh);
      setDetailOpen(true);
    } catch {
      setSelectedProposal(p);
      setDetailOpen(true);
    }
  };

  const handleProposalAction = () => {
    setDetailOpen(false);
    setSelectedProposal(null);
    fetchData();
  };

  const activeCount = proposals.filter(p => p.state === 'ACTIVE').length;
  const executedCount = proposals.filter(p => p.state === 'EXECUTED').length;

  const basescanUrl = config?.governor
    ? `https://sepolia.basescan.org/address/${config.governor}`
    : '#';

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className={embedded ? 'px-4 lg:px-6 py-6 space-y-8' : 'terminal-theme min-h-screen'} data-testid="governance-dashboard">
      <div className={embedded ? '' : 'max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8 space-y-8'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!embedded && (
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl heading-font tracking-tight font-semibold flex items-center gap-2">
              <Landmark className="h-7 w-7 text-[hsl(var(--web3-primary))]" />
              DAO Governance
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              BHermesGovernor on Base Sepolia
              <a href={basescanUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-2 hover:text-[hsl(var(--web3-primary))]">
                <ExternalLink className="h-3 w-3" /> View Contract
              </a>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/governance/parameters')} data-testid="protocol-params-btn">
            <Settings className="h-4 w-4 mr-1" />
            Protocol Params
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <CreateProposalDialog onCreated={fetchData} />
        </div>
      </div>

      {/* KPI Row */}
      {config && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="governance-kpis">
          <KpiCard
            title="Total Proposals"
            value={config.proposal_count}
            subtitle={`${activeCount} active`}
            icon={Gavel}
            testId="kpi-total-proposals"
          />
          <KpiCard
            title="Proposal Threshold"
            value={`${fmt(config.proposal_threshold)}`}
            subtitle="HERMES required"
            icon={Shield}
            testId="kpi-threshold"
          />
          <KpiCard
            title="Quorum"
            value={`${fmt(config.quorum_threshold)}`}
            subtitle="HERMES min votes"
            icon={Users}
            testId="kpi-quorum"
          />
          <KpiCard
            title="Voting Period"
            value={`${fmt(config.voting_period_blocks)}`}
            subtitle={`blocks (~${Math.round(config.voting_period_blocks * 2 / 60)} min)`}
            icon={Clock}
            testId="kpi-voting-period"
          />
        </div>
      )}

      {/* Config detail strip */}
      {config && (
        <Card data-testid="governance-config-card">
          <CardContent className="py-3 px-5">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>Voting Delay: <strong className="text-foreground">{config.voting_delay_blocks} blocks</strong></span>
              <span>Timelock: <strong className="text-foreground">{config.timelock_delay_sec}s</strong></span>
              <span>Current Block: <strong className="text-foreground font-mono">{fmt(config.current_block)}</strong></span>
              <span>Governor: <strong className="text-foreground font-mono">{config.governor?.slice(0, 8)}...{config.governor?.slice(-6)}</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Proposals list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg heading-font font-semibold">Proposals</h2>
            <Badge variant="secondary">{proposals.length} total</Badge>
          </div>

          {proposals.length === 0 ? (
            <Card data-testid="no-proposals">
              <CardContent className="py-12 flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center mb-4">
                  <Gavel className="h-7 w-7 text-[hsl(var(--web3-primary))]" />
                </div>
                <h3 className="text-base font-medium heading-font mb-1">No Proposals Yet</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  The DAO has no proposals. Token holders with {fmt(config?.proposal_threshold || 100)} HERMES can create the first one.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Tabs defaultValue="all" className="space-y-3">
              <TabsList>
                <TabsTrigger value="all">All ({proposals.length})</TabsTrigger>
                <TabsTrigger value="active">Active ({activeCount})</TabsTrigger>
                <TabsTrigger value="executed">Executed ({executedCount})</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="space-y-3">
                {proposals.map(p => <ProposalCard key={p.id} proposal={p} onSelect={handleSelectProposal} />)}
              </TabsContent>
              <TabsContent value="active" className="space-y-3">
                {proposals.filter(p => p.state === 'ACTIVE').length === 0
                  ? <p className="text-sm text-muted-foreground py-6 text-center">No active proposals</p>
                  : proposals.filter(p => p.state === 'ACTIVE').map(p => <ProposalCard key={p.id} proposal={p} onSelect={handleSelectProposal} />)
                }
              </TabsContent>
              <TabsContent value="executed" className="space-y-3">
                {proposals.filter(p => p.state === 'EXECUTED').length === 0
                  ? <p className="text-sm text-muted-foreground py-6 text-center">No executed proposals</p>
                  : proposals.filter(p => p.state === 'EXECUTED').map(p => <ProposalCard key={p.id} proposal={p} onSelect={handleSelectProposal} />)
                }
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <VotingPowerCard />

          {/* Governor Info Card */}
          <Card data-testid="governor-info-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base heading-font flex items-center gap-2">
                <Shield className="h-4 w-4 text-[hsl(var(--web3-primary))]" /> Governor Contract
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Network</span>
                <Badge variant="outline">Base Sepolia</Badge>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground mb-1">Address</p>
                <p className="font-mono text-xs break-all">{config?.governor}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Token</p>
                <p className="font-mono text-xs break-all">{config?.token}</p>
              </div>
              <Separator />
              <div className="space-y-1.5 text-xs text-muted-foreground">
                <p>Governed Actions:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Revoke/Mint Reputation SBTs</li>
                  <li>Authorize/Revoke Platforms</li>
                  <li>Update Scoring Weights</li>
                  <li>Release Emergency Funds</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Proposal Detail Modal */}
      <ProposalDetailModal
        proposal={selectedProposal}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onAction={handleProposalAction}
      />
    </div>
    {!embedded && <TerminalFloorNav />}
    </div>
  );
}
